import React, { useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Button,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const useStyles = makeStyles({
  button: { marginBottom: '1rem', marginTop: '1rem' },
  cellX5: { minWidth: '5rem' },
  cellX9: { minWidth: '9rem' },
  cellX20: { minWidth: '20rem' },
  conditions: { textAlign: 'left', whiteSpace: 'pre' },
  table: { marginBottom: '2rem' },
  tableContainer: { padding: '1rem' },
  loadingContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' },
});

const currencyFormatter = new Intl.NumberFormat('pt-br', { style: 'currency', currency: 'BRL' });
const dateFormatter = date => DateTime.fromISO(date).setLocale('pt-BR').toLocaleString(DateTime.DATE_SHORT);
const phoneFormatter = phone => phone.replace(/(\d{2})(\d{2})(\d{4,5})(\d{4})/, '+$1 ($2) $3-$4');

export function UbabyList() {
  const [rows, setRows] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(DateTime.now().startOf('month').toISODate());
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false); // New loading state for payments
  const classes = useStyles();
  const navigate = useNavigate();

  const transformData = data => {
    return data.map(row => ({
      ...row,
      raw_first_payment: row.first_payment,
      raw_amount_owed: row.amount_owed,

      first_payment: currencyFormatter.format(row.first_payment),
      amount_owed: currencyFormatter.format(row.amount_owed),
      created_at: row.created_at ? dateFormatter(row.created_at) : '-',
      first_payday: row.first_payday ? dateFormatter(row.first_payday) : '-',
      phone_number: phoneFormatter(row.phone_number),
      raw_phone_number: row.phone_number,
    }));
  };

  const fetchData = useCallback(async month => {
    setRows([]);
    setLoading(true);
    try {
      const { data } = await api.get(`/ubabies/${month}`);
      return transformData(data);
    } catch (error) {
      console.error('Error fetching data:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const transformedData = await fetchData(selectedMonth);
      setRows(transformedData);
    })();
  }, [selectedMonth, fetchData]);

  const getLast12Months = () => {
    const months = [];
    for (let i = 0; i < 12; i += 1) {
      const date = DateTime.now().minus({ months: i }).startOf('month');
      months.push({
        value: date.toISODate(),
        label: date.toFormat('MMMM yyyy'),
      });
    }
    return months;
  };

  const handleMonthChange = event => {
    setSelectedMonth(event.target.value);
  };

  const handlePayment = async (ubaby_id, type, amount) => {
    setPaymentLoading(true);
    try {
      const response = await api.post(`/payments`, { ubaby_id, type, amount });
      if (response.status === 204) {
        setRows(prevRows => prevRows.filter(row => row.id !== ubaby_id));
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Error processing payment. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await api.get(`/ubabies/${selectedMonth}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ubabies_${selectedMonth}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Error downloading CSV. Please try again.');
    }
  };
  return (
    <>
      <div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <FormControl>
          <InputLabel id='month-select-label'>Month</InputLabel>
          <Select
            labelId='month-select-label'
            value={selectedMonth}
            label='Month'
            onChange={handleMonthChange}
            style={{ marginBottom: '1rem' }}
          >
            {getLast12Months().map(month => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          onClick={handleDownloadCSV}
          variant='contained'
          color='warning'
          size='large'
          style={{ marginBottom: '1rem' }}
        >
          Download .CSV
        </Button>
      </div>
      <Paper>
        <TableContainer className={classes.tableContainer}>
          {loading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress size={24} />
            </div>
          ) : (
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell align='center'>Id</TableCell>
                  <TableCell align='center'>Nome</TableCell>
                  <TableCell align='center'>Número</TableCell>
                  <TableCell align='center'>Países</TableCell>
                  <TableCell align='center'>PIX</TableCell>
                  <TableCell align='center'>Data de entrada</TableCell>
                  <TableCell align='center'>Acumulado na 1ª semana</TableCell>
                  <TableCell align='center'>Data do pagamento de 1ª semana (previsto)</TableCell>
                  <TableCell align='center'>Data do pagamento de 1ª semana (realizado)</TableCell>
                  <TableCell align='center'>Valor devido</TableCell>
                  <TableCell align='center'>Data do último pagamento mensal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={`${row.id}-${selectedMonth}`}>
                    {' '}
                    {/* Id */}
                    <TableCell align='center'>{row.id}</TableCell>
                    {/* Name */}
                    <TableCell
                      align='center'
                      onClick={() => navigate(`/ubaby-details/${row.raw_phone_number}/${selectedMonth}`)}
                    >
                      <div className={classes.cellX9}>
                        <a href={`/ubaby-details/${row.raw_phone_number}/${selectedMonth}`}>{row.name}</a>
                      </div>
                    </TableCell>
                    {/* Number */}
                    <TableCell align='center'>
                      <div className={classes.cellX9}>{row.phone_number}</div>
                    </TableCell>
                    {/* Countries */}
                    <TableCell align='center'>{row.countries.join(', ')}</TableCell>
                    {/* PIX */}
                    <TableCell align='center'>
                      <div className={classes.cellX9}>{row.pix}</div>
                    </TableCell>
                    {/* Created at */}
                    <TableCell align='center'>{row.created_at}</TableCell>
                    {/* First payment */}
                    <TableCell align='center'>
                      {row.first_payment}
                      <Button
                        variant='contained'
                        color='primary'
                        size='small'
                        disabled={paymentLoading || !row.raw_first_payment}
                        onClick={() => handlePayment(row.id, 'first week', row.raw_first_payment)}
                        style={{ marginLeft: '8px', marginTop: '8px' }}
                      >
                        Pago
                      </Button>
                    </TableCell>
                    {/* First payday (expected) */}
                    <TableCell align='center'>{row.first_payment === 0 ? '-' : row.first_payday}</TableCell>
                    {/* First payday (actual) */}
                    <TableCell align='center'>{row.payments.find(p => p.type === 'first week')?.date ?? '-'}</TableCell>
                    {/* Amount owed */}
                    <TableCell align='center'>
                      {row.amount_owed}
                      <Button
                        variant='contained'
                        color='secondary'
                        size='small'
                        disabled={paymentLoading || !row.raw_amount_owed}
                        onClick={() => handlePayment(row.id, 'regular', row.raw_amount_owed)}
                        style={{ marginLeft: '8px', marginTop: '8px' }}
                      >
                        Pago
                      </Button>
                    </TableCell>
                    {/* Last monthly payment */}
                    <TableCell align='center'>
                      <div className={classes.cellX5}>
                        {row.payments.find(p => p.type === 'regular')?.date
                          ? dateFormatter(row.payments.find(p => p.type === 'regular').date)
                          : '-'}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>
    </>
  );
}
